import React from "react";
import { Hero, ManagePlan, QuickView } from "../../components/clientServicing";
import * as clientActions from "../../actions/client";
import * as AccountActions from "../../actions/account";
import { useActions } from "../../actions";
import { useSelector } from "react-redux";
import { PageTitle } from "../../components/formInputs";
import kaiZenTheme, { KaiZenThemeGlobalCss } from "../../styles/theme/kaiZenTheme";
import { MuiThemeProvider } from "@material-ui/core/styles";
import moment from "moment";
import { RootState } from "../../reducers";
import { DATE_FORMAT_SERVICE_DASHBOARD } from "../../utils/dateUtils";
import ServicingFeedback from "./ServicingFeedback";
import { AccountType } from "../../constants";
import { Link, Typography } from "@material-ui/core";

export const nextAnniversaryDate = (issueDate:string) => {
    const issueDateFormatted = moment(issueDate).format(DATE_FORMAT_SERVICE_DASHBOARD)
    const month = moment(issueDateFormatted).format('MM')
    const day   = moment(issueDateFormatted).format('DD');
    const currentYearIssueDate = `${month}/${day}/${moment().year()}`;
    const days = moment().diff(currentYearIssueDate, 'days');
    if (Math.sign(days) > 0) {
        return moment(`${month}/${day}/${moment().year()+1}`).format(DATE_FORMAT_SERVICE_DASHBOARD);
    }
    return moment(`${month}/${day}/${moment().year()}`).format(DATE_FORMAT_SERVICE_DASHBOARD);
}

export const lastAnniversaryDate = (issueDate:string) => {
    const issueDateFormatted = moment(issueDate).format(DATE_FORMAT_SERVICE_DASHBOARD)
    const month = moment(issueDateFormatted).format('MM')
    const day   = moment(issueDateFormatted).format('DD');
    const currentYearIssueDate = `${month}/${day}/${moment().year()}`;
    const days = moment().diff(currentYearIssueDate, 'days');
    if (Math.sign(days) > 0) {
        return moment(`${month}/${day}/${moment().year()}`).format(DATE_FORMAT_SERVICE_DASHBOARD);
    }
    return moment(`${month}/${day}/${moment().year()-1}`).format(DATE_FORMAT_SERVICE_DASHBOARD);
}
export const completedPolicyYear = (issueDate: string) => { // this will return the policy year that is completed
    const anniversaryDate = nextAnniversaryDate(issueDate);
    const issueDateObj = moment(issueDate).format(DATE_FORMAT_SERVICE_DASHBOARD);
    return moment(anniversaryDate, DATE_FORMAT_SERVICE_DASHBOARD).diff(issueDateObj, 'years') - 1;
}

export const getCurrentPolicyYearCount = (issueDate: string) => {
    const anniversaryDate = nextAnniversaryDate(issueDate);
    const issueDateObj = moment(issueDate).format(DATE_FORMAT_SERVICE_DASHBOARD);
    return moment(anniversaryDate, DATE_FORMAT_SERVICE_DASHBOARD).diff(issueDateObj, 'years');
}

export const getIndexOfCompletedYear = (issueDate: string) => {
    const policyArrayIndex = completedPolicyYear(issueDate) - 1;
    if(policyArrayIndex < 0){
        return 0;
    }
    return policyArrayIndex;
}

export const getIndexOfPolicyIllustratedYear = (issueDate: string, policyCompletedYearIndex: number) => {
    return completedPolicyYear(issueDate) <= 0 ? 0 : policyCompletedYearIndex + 1; // handle case if completed policy year is <= 0
}

export const getCurrentPolicyYear = (anniversaryDate:string) => {
    let anniversaryDateObj = moment(anniversaryDate, DATE_FORMAT_SERVICE_DASHBOARD);
    return Number(anniversaryDateObj.format('YYYY')) - 1;
}
// Policy Performance Year is also known as Completed year
export const getPolicyPerformanceYear = (anniversaryDate: string) => {
    let anniversaryDateObj = moment(anniversaryDate, DATE_FORMAT_SERVICE_DASHBOARD);
    const month = moment(anniversaryDateObj).format('MM')
    const day = moment(anniversaryDateObj).format('DD');
    let yearFrom: number = Number(anniversaryDateObj.format('YYYY')) - 1;
    let yearTo: number = Number(anniversaryDateObj.format('YYYY')) - 2;
    let to = moment(`${month}/${day}/${yearTo}`).format(DATE_FORMAT_SERVICE_DASHBOARD);
    let from = moment(`${month}/${day}/${yearFrom}`).format(DATE_FORMAT_SERVICE_DASHBOARD);
    return `${to} - ${from}`;
}

function getNumberWithSuffix(num: number): string {
    const suffixes = ["th", "st", "nd", "rd"];
    const remainder = num % 100;

    // Handle special cases for 11-13
    if (remainder >= 11 && remainder <= 13) {
        return `${num}th`;
    }

    // Get the appropriate suffix for other numbers
    const lastDigit = num % 10;
    const suffix = suffixes[lastDigit] || suffixes[0];

    return `${num}${suffix}`;
}

export const getAnticipatedPolicyPerformanceYear = (anniversaryDate: string) => {
    const anniversaryDateObj = moment(anniversaryDate, DATE_FORMAT_SERVICE_DASHBOARD);
    const month = moment(anniversaryDateObj).format('MM')
    const day = moment(anniversaryDateObj).format('DD');
    const lastAnniversaryYear = Number(anniversaryDateObj.format('YYYY')) - 1;
    const from = moment(`${month}/${day}/${lastAnniversaryYear}`).format(DATE_FORMAT_SERVICE_DASHBOARD);
    let nextAnniversaryYear: number = Number(anniversaryDateObj.format('YYYY'));
    let to = moment(`${month}/${day}/${nextAnniversaryYear}`).format(DATE_FORMAT_SERVICE_DASHBOARD);
    return `${from} - ${to}`;
}

export default function ClientServiceDashbaord() {
    const [clientDetail, setClientDetail] = React.useState(undefined);
    const { getClientCaseDetails } = useActions(clientActions);
    const { getProfileDetails } = useActions(AccountActions);
    const clientCaseDetails = useSelector((state: RootState) => state.clientCaseDetails);
    const issueDate = clientCaseDetails && clientCaseDetails.tranche && clientCaseDetails.tranche.issueDate;
    const nextAnniversary = nextAnniversaryDate(issueDate);
    const completedPolicyYearCount = completedPolicyYear(issueDate);
    const {role} = useSelector((state: RootState) => state.auth.user);

    let currentPolicyYear = getCurrentPolicyYearCount(issueDate);
    let numberWithSubtext = getNumberWithSuffix(currentPolicyYear);
    console.log(currentPolicyYear);
    let policyNumberSubText = `(${numberWithSubtext} year anticipated values)`;
    if (completedPolicyYearCount > 0) {
        policyNumberSubText = `(Policy Year ${currentPolicyYear} / (${getPolicyPerformanceYear(nextAnniversary)}))`;
    }
    policyNumberSubText = policyNumberSubText + " " + `Year ${currentPolicyYear} credits (growth) are applied at the beginning of the following policy year.`
    const updateCss = () => {
        let frontendCss = document.getElementById("frontendCss");
        frontendCss && frontendCss.setAttribute("href", "/css/clientProfileCss.css");
    };

    React.useEffect(() => {
        updateCss();
        getClientDetails();
        return () => {
            updateCss();
        }
    }, []);

    const getClientDetails = async () => {
        await getClientCaseDetails();
        const details = await getProfileDetails();
        setClientDetail(details);
    };

    return (
            <MuiThemeProvider theme={kaiZenTheme}>
                <KaiZenThemeGlobalCss/>
                <link rel="stylesheet" href={`${process.env.PUBLIC_URL}/css/clientProfileCss.css`}/>
                <PageTitle title="Client Dashboard"/>
                <Hero clientDetail={clientDetail}/>
                <QuickView
                        caseDetail={clientCaseDetails}
                        clientDetail={clientDetail} policyNumberSubText={policyNumberSubText}/>
                <ManagePlan/>
                {
                        (role !== AccountType.Individual && role !== AccountType.Multilife) &&
                        <ServicingFeedback/>
                }

                <Typography variant="body1" className="mt35 ">Should you believe
                    there is any discrepancy please contact Kai-Zen Support at 800-294-9940 or
                    email <Link href="mailto:info@kaizenplan.com">info@kaizenplan.com</Link>.
                    For more detailed information about your life insurance policy, reach out to
                    your carrier. Past performance does not guarantee future results. Guarantees are backed by the
                    financial strength and claims paying ability of the issuing company.</Typography>

            </MuiThemeProvider>
    );
}